import { Alert, SkeletonRows, Tabs, TabsContent, TabsList, TabsTrigger } from "@station/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { usePlatformPermissions, usePlatformTenant } from "../../../features/platform/queries.js";
import { PlatformPage, TenantStatusBadge } from "../../../features/platform/shared.js";
import {
  TenantDataTab,
  TenantFeaturesTab,
  TenantInvoicesTab,
  TenantOverviewTab,
  TenantSubscriptionTab,
  TenantSupportTab,
} from "../../../features/platform/tenant-tabs.js";
import { errorMessage } from "../../../lib/errors.js";

const TABS = ["general", "subscription", "invoices", "features", "support", "data"] as const;
type Tab = (typeof TABS)[number];

const searchSchema = z.object({ tab: z.enum(TABS).optional().catch(undefined) });

export const Route = createFileRoute("/platform/kiracilar/$id")({
  validateSearch: (search) => searchSchema.parse(search),
  component: TenantDetailRoute,
});

/**
 * Kiracı ayrıntısı (Prompt 24 web): genel bilgi + durum, abonelik (plan, indirim, kullanım, önizleme, fatura profili),
 * faturalar ve tahsilat, özellikler (plan / yayın / geçersiz kılma, istasyon kısıtı, ajan kanalı), destek erişimi, veri
 * (dışa aktarım, kapanış, anonimleştirme). Sekme URL'de tutulur.
 */
function TenantDetailRoute(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = Route.useParams();
  const search = Route.useSearch();
  const tab: Tab = search.tab ?? "general";
  const permissions = usePlatformPermissions();
  const tenant = usePlatformTenant(id);
  const canBilling = permissions.has("platform.billing.read");
  const visible = TABS.filter((value) => canBilling || (value !== "subscription" && value !== "invoices"));

  return (
    <PlatformPage
      title={tenant.data?.name ?? t("platform.tenants.detail")}
      breadcrumb={[
        {
          label: t("platform.nav.tenants"),
          link: (children, className) => (
            <Link to="/platform/kiracilar" className={className}>
              {children}
            </Link>
          ),
        },
        { label: tenant.data?.name ?? "…" },
      ]}
      permission="platform.tenants.read"
      actions={tenant.data ? <TenantStatusBadge status={tenant.data.status} /> : null}
    >
      {tenant.error ? (
        <Alert tone="danger">{errorMessage(tenant.error)}</Alert>
      ) : tenant.isPending ? (
        <SkeletonRows rows={6} />
      ) : (
        <Tabs
          className="grid gap-4"
          value={visible.includes(tab) ? tab : "general"}
          onValueChange={(next) =>
            void navigate({
              to: ".",
              search: ((previous: Record<string, unknown>) => ({ ...previous, tab: next })) as never,
              replace: true,
            })
          }
        >
          <TabsList aria-label={t("platform.tenants.tabsLabel")} className="flex-wrap">
            {visible.map((value) => (
              <TabsTrigger key={value} value={value}>
                {t(`platform.tenants.tab.${value}`)}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="general">
            <TenantOverviewTab tenant={tenant.data} />
          </TabsContent>
          {canBilling ? (
            <>
              <TabsContent value="subscription">
                <TenantSubscriptionTab tenantId={id} />
              </TabsContent>
              <TabsContent value="invoices">
                <TenantInvoicesTab tenant={tenant.data} />
              </TabsContent>
            </>
          ) : null}
          <TabsContent value="features">
            <TenantFeaturesTab tenantId={id} />
          </TabsContent>
          <TabsContent value="support">
            <TenantSupportTab tenant={tenant.data} />
          </TabsContent>
          <TabsContent value="data">
            <TenantDataTab tenant={tenant.data} />
          </TabsContent>
        </Tabs>
      )}
    </PlatformPage>
  );
}
